import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2, FileDown, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ab_audit_modal_state_v1";
const SCROLL_TRIGGER = 0.6; // 60% scroll
const TIME_FALLBACK_MS = 30_000; // fire after 30s if no other trigger

const BED_RANGES = ["Clinic / OPD only", "10–50 beds", "50–150 beds", "150–500 beds", "500+ beds"];

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeState(patch) {
  try {
    const next = { ...readState(), ...patch, updated: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export default function AuditModal() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ email: "", organization: "", bed_count: "" });
  const armedRef = useRef(true);

  // Should we even arm the modal? (skip if submitted or recently dismissed)
  useEffect(() => {
    const s = readState();
    if (s.submitted) { armedRef.current = false; return; }
    if (s.dismissedAt && Date.now() - s.dismissedAt < 1000 * 60 * 60 * 24 * 7) {
      armedRef.current = false; return;
    }
  }, []);

  // Triggers: scroll depth, exit-intent (desktop), time fallback
  useEffect(() => {
    if (!armedRef.current) return;
    let fired = false;

    const fire = (reason) => {
      if (fired || !armedRef.current) return;
      // Don't fire while user is already inside the contact form
      const contact = document.getElementById("contact");
      if (contact) {
        const rect = contact.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.6 && rect.bottom > 0) return;
      }
      fired = true;
      writeState({ lastTrigger: reason, lastTriggerAt: Date.now() });
      setOpen(true);
    };

    const onScroll = () => {
      const h = document.documentElement;
      const ratio = (h.scrollTop + window.innerHeight) / h.scrollHeight;
      if (ratio >= SCROLL_TRIGGER) fire("scroll");
    };

    const onMouseLeave = (e) => {
      if (e.clientY <= 0 && window.innerWidth >= 900) fire("exit-intent");
    };

    const t = setTimeout(() => fire("time"), TIME_FALLBACK_MS);

    window.addEventListener("scroll", onScroll, { passive: true });
    document.documentElement.addEventListener("mouseleave", onMouseLeave);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close("esc"); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = (reason = "x") => {
    setOpen(false);
    if (!done) writeState({ dismissedAt: Date.now(), dismissedReason: reason });
  };

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please add your email"); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API}/audit-leads`, { ...form, source: "audit_modal" });
      writeState({ submitted: true, submittedAt: Date.now() });
      setDone(true);
      toast.success("Audit on its way — check your inbox in a few minutes.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="audit-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => close("backdrop")}
            className="absolute inset-0 bg-ab-primary/35 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl bg-ab-bg rounded-3xl overflow-hidden shadow-[0_40px_100px_-30px_rgba(13,64,53,0.55)] grid grid-cols-1 md:grid-cols-12"
          >
            <button
              onClick={() => close("x")}
              data-testid="audit-modal-close"
              aria-label="Close"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white border border-ab-border flex items-center justify-center text-ab-primary transition"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Left side — story column */}
            <div className="md:col-span-5 bg-ab-primary text-white p-8 md:p-10 relative overflow-hidden">
              <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-ab-accent/30 blur-3xl" />
              <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] tracking-[0.2em] uppercase">
                  <FileDown className="w-3.5 h-3.5" /> Free PDF
                </div>
                <h3 className="font-serif text-3xl md:text-4xl mt-5 leading-tight">
                  The 5-min hospital efficiency audit.
                </h3>
                <p className="mt-4 text-white/75 text-sm leading-relaxed">
                  A founder-curated checklist of the 17 places hospitals quietly bleed time and money — with the exact PranaOs module that closes each gap.
                </p>
                <ul className="mt-7 space-y-3 text-sm text-white/85">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-ab-accent mt-0.5 shrink-0" /> Wait time & TAT diagnostic</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-ab-accent mt-0.5 shrink-0" /> Discharge & billing leakage map</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-ab-accent mt-0.5 shrink-0" /> ABDM & compliance scorecard</li>
                </ul>
                <div className="mt-10 flex items-center gap-2 text-xs text-white/55">
                  <ShieldCheck className="w-3.5 h-3.5" /> No spam · single email · unsubscribe anytime
                </div>
              </div>
            </div>

            {/* Right side — form / success */}
            <div className="md:col-span-7 bg-white p-8 md:p-10 relative">
              {!done ? (
                <form onSubmit={submit} data-testid="audit-modal-form">
                  <p className="ab-eyebrow text-ab-accent">Get the audit</p>
                  <h4 className="font-serif text-3xl md:text-4xl mt-3 text-ab-primary leading-tight">
                    Where is your hospital <span className="italic font-light">losing</span> the most?
                  </h4>
                  <p className="mt-3 text-sm text-ab-muted">
                    We'll send the audit to your inbox in seconds. Reading time: 5 minutes. Decisions you can make: many.
                  </p>

                  <div className="mt-7 space-y-4">
                    <label className="block">
                      <span className="ab-eyebrow text-ab-muted block mb-2">Work email <span className="text-ab-accent">*</span></span>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        data-testid="audit-modal-email"
                        className="ab-input"
                        placeholder="aisha@hospital.com"
                        autoFocus
                      />
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="ab-eyebrow text-ab-muted block mb-2">Organization</span>
                        <input
                          value={form.organization}
                          onChange={update("organization")}
                          data-testid="audit-modal-org"
                          className="ab-input"
                          placeholder="Lifeline Hospitals"
                        />
                      </label>
                      <label className="block">
                        <span className="ab-eyebrow text-ab-muted block mb-2">Bed range</span>
                        <select
                          value={form.bed_count}
                          onChange={update("bed_count")}
                          data-testid="audit-modal-beds"
                          className="ab-input"
                        >
                          <option value="">Select…</option>
                          {BED_RANGES.map((b) => <option key={b}>{b}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>

                  <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => close("no-thanks")}
                      data-testid="audit-modal-decline"
                      className="text-sm text-ab-muted hover:text-ab-text underline-offset-4 hover:underline"
                    >
                      No thanks, I'll keep bleeding
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="audit-modal-submit"
                      className="ab-pill-btn group disabled:opacity-60"
                    >
                      {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending</>) : (<>Send me the audit <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></>)}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="h-full flex flex-col justify-center" data-testid="audit-modal-success">
                  <div className="w-12 h-12 rounded-full bg-ab-accent/15 text-ab-accent flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h4 className="font-serif text-3xl md:text-4xl mt-5 text-ab-primary leading-tight">
                    On its way to <span className="italic font-light">{form.email || "your inbox"}</span>.
                  </h4>
                  <p className="mt-3 text-ab-muted">
                    While you wait — a founder will personally read your reply if you ever hit "respond" to that email.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => close("done")}
                      data-testid="audit-modal-close-success"
                      className="ab-pill-btn-ghost"
                    >
                      Close
                    </button>
                    <a
                      href="/audit"
                      target="_blank"
                      rel="noreferrer"
                      data-testid="audit-modal-open-pdf"
                      className="ab-pill-btn"
                    >
                      Open the audit <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
